import {
    AddressModel,
    assignModelOptions,
    BooleanModel,
    ClientManagerInstanceToken,
    CompositeModel,
    ConfigConstants,
    ConfigService,
    CseDopModel,
    CustomInjector,
    DisplayDecorator,
    DropdownModel,
    isDefined,
    MaterialCentralReturnModel,
    MaterialService,
    MaterialUtilitiesService,
    ModelOptionsLoaderService,
    NotificationService,
    RadioModel,
    RadioModelOptions,
    TextModel,
} from '@evorait/pace-core';
import { TranslateService } from '@ngx-translate/core';
import * as _ from 'lodash';

export default class MaterialReturnP58Model extends CompositeModel {
    public materialReturnList: MaterialCentralReturnModel[];
    public awbReturn: TextModel; // to be able to search return material via awb
    public qmNum: TextModel; // needed for "Add new material" to input notification num
    public maNum: TextModel; // needed for "Add new material" to input task num
    public returnInfo: TextModel;
    public noPhysicalReturn: DisplayDecorator<BooleanModel>;
    public deliveryReturnOptions: RadioModel<TextModel>;
    public dropOffPointOptions: DropdownModel<CseDopModel>;
    public customerAddressOptions: DropdownModel<CseDopModel>;
    public isInfoSaved = false;
    public isAWBPackagingEnabled = false;
    public awbPackaging: TextModel;
    public selectedMaterialsNumber: number;
    public enableStatesOfUSADropdown: boolean;

    /**
     * Prepare a model for the Material Return screen.
     */
    public constructor() {
        super('MaterialReturnModel');
        const injector = CustomInjector.getInjector();
        const configService = injector.get(ConfigService);
        const modelOptionsLoader = injector.get(ModelOptionsLoaderService);
        const translate = injector.get(TranslateService);
        const notificationService = injector.get(NotificationService);
        const materialService = injector.get(MaterialService);
        const clientManager = injector.get(ClientManagerInstanceToken);
        const materialUtilities = injector.get(MaterialUtilitiesService);

        this.enableStatesOfUSADropdown = configService.getEnableStatesOfUSADropdown();
        this.materialReturnList = materialService.getAllReturnsFromLocalStorage();
        this.awbReturn = new TextModel('awbReturn');
        modelOptionsLoader.load(this.awbReturn, 'MaterialReturn', 'AWB');
        this.awbReturn = assignModelOptions(
            this.awbReturn,
            {
                ...this.awbReturn.options,
                required: false,
            },
            this,
        );

        this.isAWBPackagingEnabled = configService.getEnableAWBPackaging();
        if (this.isAWBPackagingEnabled) {
            this.awbPackaging = new TextModel('awbPackaging');
            modelOptionsLoader.load(this.awbPackaging, 'MaterialReturn', 'PkgAwbnr');
            this.awbPackaging = assignModelOptions(
                this.awbPackaging,
                {
                    ...this.awbPackaging.options,
                    required: false,
                },
                this,
            );
        }

        this.qmNum = new TextModel('NotificationNo', '');
        modelOptionsLoader.load(this.qmNum, 'MaterialReturn', 'NotificationNumber');
        this.maNum = new TextModel('TaskNumber', '');
        modelOptionsLoader.load(this.maNum, 'MaterialReturn', 'TaskNumber');

        this.returnInfo = new TextModel('MatReturnText');
        modelOptionsLoader.load(this.returnInfo, 'MaterialReturn', 'ReturnInfo');
        this.noPhysicalReturn = new DisplayDecorator<BooleanModel>(
            <string>translate.instant('MATERIAL_RETURN_NO_PHYSICAL_RETURN_LABEL'),
            new BooleanModel('MatIndicator'),
        );
        modelOptionsLoader.load(this.noPhysicalReturn, 'MaterialReturn', 'NoPhysicalReturn');

        const addDeliveryAddressTaxi = configService.getAddDeliveryAddressTaxi();

        // build the radio button option for address
        const deliveryOptions: Array<DisplayDecorator<TextModel>> = [];
        const materialReturnAddressOptions = configService.getMaterialReturnAddressOptions();
        if (materialReturnAddressOptions.includes(ConfigConstants.returnCustomer.toLowerCase())) {
            deliveryOptions.push(
                new DisplayDecorator(
                    <string>translate.instant('MATERIAL_DELIVERY_ADDRESS_OPTION_CUSTOMER'),
                    new TextModel(ConfigConstants.returnCustomer, ConfigConstants.returnCustomer),
                ),
            );
        }
        if (materialReturnAddressOptions.includes(ConfigConstants.returnDop.toLowerCase())) {
            deliveryOptions.push(
                new DisplayDecorator(
                    <string>translate.instant('MATERIAL_DELIVERY_ADDRESS_OPTION_DOP'),
                    new TextModel(ConfigConstants.returnDop, ConfigConstants.returnDop),
                ),
            );
        }
        if (addDeliveryAddressTaxi && materialReturnAddressOptions.includes(ConfigConstants.returnTaxi.toLowerCase())) {
            deliveryOptions.push(
                new DisplayDecorator(
                    <string>translate.instant('MATERIAL_DELIVERY_ADDRESS_OPTION_TAXI'),
                    new TextModel(ConfigConstants.returnTaxi, ConfigConstants.returnTaxi),
                ),
            );
        }
        if (clientManager.isNgmP58OrSA()) {
            deliveryOptions.push(
                new DisplayDecorator(
                    <string>translate.instant('MATERIAL_DELIVERY_ADDRESS_OPTION_N/A'),
                    new TextModel(ConfigConstants.returnNotSelected, ConfigConstants.returnNotSelected),
                ),
            );
        }
        deliveryOptions.findIndex((val) => val.displayName === ConfigConstants.optionNA);
        this.deliveryReturnOptions = new RadioModel<TextModel>(
            'DeliveryAddress',
            deliveryOptions,
            new RadioModelOptions({
                initialSelection: materialUtilities.getInitialSelectionForRadio(deliveryOptions),
                invalidSelection: materialUtilities.getInvalidSelectionForRadio(deliveryOptions),
            }),
        );

        this.dropOffPointOptions = new DropdownModel('DropOffPointsOptions', materialService.getCSEDopList());
        modelOptionsLoader.load(this.dropOffPointOptions, 'MaterialReturn', 'DOPDropdown');
        this.dropOffPointOptions = assignModelOptions(
            this.dropOffPointOptions,
            {
                ...this.dropOffPointOptions.options,
                required: true,
            },
            this,
        );

        // prepare customer address list and consider those materials address
        // which are already saved/selected and ready to be returned
        // First priority is to be given for the material which are already selected/saved
        // https://evorait.atlassian.net/browse/SHCNGMP58-2315

        // If no material are selected/saved then we need the address of all the material that are available for material return
        // https://evorait.atlassian.net/browse/SHCNGMP58-2379
        let addressesList: AddressModel[] = [];

        const selectedMaterial = this.materialReturnList.find(
            (materialCentralReturn) => materialCentralReturn.material.isRowSelected.value,
        );

        if (selectedMaterial) {
            addressesList = addressesList.concat(selectedMaterial.material.customerAddress);
        } else {
            addressesList = this.materialReturnList
                .map((materialCentralReturn) => {
                    return materialCentralReturn.material.customerAddress;
                })
                .filter((address) => isDefined(address));
        }

        addressesList = addressesList.concat(notificationService.customerAddresses);
        addressesList = _.uniqBy(addressesList, 'addrNumber.value');
        if (!addressesList.length) {
            // add empty model to enable user to edit a customer's address
            addressesList.push(AddressModel.build({ name1: '' }));
        }
        const customerAddressesList = addressesList
            .filter((a) => !!a)
            .map((address) => {
                const cseDopModel = CseDopModel.fromAddressModel(address);
                if (this.enableStatesOfUSADropdown) {
                    const isDopAddress =
                        this.deliveryReturnOptions.selectedEntry.model.name === ConfigConstants.returnDop;
                    AddressModel.updateStateOfUSA(cseDopModel, '', isDopAddress);
                }
                cseDopModel.setFieldValidations('MaterialReturn');
                return new DisplayDecorator(`${cseDopModel.name1Long} ${cseDopModel.name2Long}`, cseDopModel);
            });
        this.customerAddressOptions = new DropdownModel('CustomerAddressOptions', customerAddressesList);
        modelOptionsLoader.load(this.customerAddressOptions, 'MaterialReturn', 'CustomerAddressDropdown');
        this.setSavedValues();
    }

    private setSavedValues(): void {
        const savedMaterial = this.materialReturnList.find(
            (materialReturnModel) =>
                materialReturnModel.material.isRowSelected.value &&
                materialReturnModel.material.deliveryReturnSelectedOption,
        );

        if (savedMaterial) {
            this.isInfoSaved = true;
            this.returnInfo.value = savedMaterial.returnInfo.value;
            this.noPhysicalReturn.value = savedMaterial.noPhysicalReturn.value;
            if (savedMaterial.material.deliveryReturnSelectedOption) {
                this.deliveryReturnOptions.selectByPredicate(
                    (entry) => entry.model.name === savedMaterial.material.deliveryReturnSelectedOption,
                );
                if (
                    this.deliveryReturnOptions.selectedEntry.model.name === ConfigConstants.returnDop &&
                    savedMaterial.material.dropOffPointSelectedOption
                ) {
                    this.dropOffPointOptions.selectByPredicate(
                        (entry) => entry.model.dropOffPoint.value === savedMaterial.material.dropOffPointSelectedOption,
                    );
                }
                if (
                    this.deliveryReturnOptions.selectedEntry.model.name === ConfigConstants.returnCustomer &&
                    savedMaterial.material.customerAddress
                ) {
                    this.customerAddressOptions.selectByPredicate(
                        (entry) =>
                            entry.model.name1.value === savedMaterial.material.customerAddress.name1.value ||
                            entry.model.name1Long === savedMaterial.material.customerAddress.name1.value,
                    );
                }
            }
        }
    }

    // Keeps track of material selected and the No Physical Return checkbox value
    // If no physical return is selected, then AWB Packaging field is not mandatory but
    // if more than one material is selected and no physical return is unchecked then
    // AWB Packaging field is mandatory
    public updateAWBPackagingModelOptions(): void {
        if (!this.isAWBPackagingEnabled) return;

        let required = false;
        if (this.selectedMaterialsNumber) {
            required = this.selectedMaterialsNumber > 1;
        }

        this.awbPackaging = assignModelOptions(this.awbPackaging, {
            ...this.awbPackaging.options,
            required,
        });
    }
}
